/**
 * The attachments bucket is private: a stored file URL (public, signed or
 * authenticated form) does not open by itself. Every record read through the
 * data provider goes through `signStorageUrls`, which swaps each URL of the
 * bucket for a short-lived signed URL. Supabase only signs files the user may
 * read (storage policies: their team's folder), so a file of another team
 * keeps a URL that does not open.
 */

/** Lifetime of a signed URL; lists refetch (and re-sign) long before it ends. */
export const SIGNED_URL_TTL_SECONDS = 60 * 60;
/** A cached signed URL is reused only while it has this much life left. */
const REUSE_MARGIN_MS = 10 * 60 * 1000;

const STORAGE_OBJECT_URL =
  /\/storage\/v1\/object\/(?:public|sign|authenticated)\//;

/** Path of the file inside `bucket` for a storage URL of that bucket, else null. */
export const extractBucketPath = (
  value: string,
  bucket: string,
): string | null => {
  if (!STORAGE_OBJECT_URL.test(value)) return null;
  let pathname: string;
  try {
    pathname = new URL(value).pathname;
  } catch {
    return null;
  }
  const match = pathname.match(
    new RegExp(
      `/storage/v1/object/(?:public|sign|authenticated)/${bucket}/(.+)$`,
    ),
  );
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

/** Every bucket path referenced by a string anywhere in `value`. */
export const collectBucketPaths = (
  value: unknown,
  bucket: string,
): string[] => {
  const paths = new Set<string>();
  const visit = (node: unknown) => {
    if (typeof node === "string") {
      const path = extractBucketPath(node, bucket);
      if (path) paths.add(path);
    } else if (Array.isArray(node)) {
      node.forEach(visit);
    } else if (node && typeof node === "object" && !(node instanceof Blob)) {
      Object.values(node).forEach(visit);
    }
  };
  visit(value);
  return [...paths];
};

/** A copy of `value` where each bucket URL with a signed URL is replaced. */
export const replaceBucketUrls = <T>(
  value: T,
  bucket: string,
  signedUrls: ReadonlyMap<string, string>,
): T => {
  const map = (node: unknown): unknown => {
    if (typeof node === "string") {
      const path = extractBucketPath(node, bucket);
      return (path && signedUrls.get(path)) ?? node;
    }
    if (Array.isArray(node)) return node.map(map);
    if (node && typeof node === "object" && !(node instanceof Blob)) {
      return Object.fromEntries(
        Object.entries(node).map(([key, child]) => [key, map(child)]),
      );
    }
    return node;
  };
  return map(value) as T;
};

export interface SignedUrlSigner {
  createSignedUrls: (
    paths: string[],
    expiresIn: number,
  ) => Promise<{
    data:
      | { path: string | null; signedUrl: string; error: string | null }[]
      | null;
    error: unknown;
  }>;
}

/**
 * Builds a function that signs the bucket URLs of any value, reusing signed
 * URLs that still have time left so refetches do not re-sign every file.
 */
export const createStorageUrlSigner = (
  bucket: string,
  getSigner: () => SignedUrlSigner,
  now: () => number = Date.now,
) => {
  const cache = new Map<string, { url: string; expiresAt: number }>();

  return async <T>(value: T): Promise<T> => {
    const paths = collectBucketPaths(value, bucket);
    if (paths.length === 0) return value;

    const signedUrls = new Map<string, string>();
    const missing: string[] = [];
    for (const path of paths) {
      const cached = cache.get(path);
      if (cached && cached.expiresAt - REUSE_MARGIN_MS > now()) {
        signedUrls.set(path, cached.url);
      } else {
        missing.push(path);
      }
    }

    if (missing.length > 0) {
      const { data, error } = await getSigner().createSignedUrls(
        missing,
        SIGNED_URL_TTL_SECONDS,
      );
      if (error) {
        console.error("Could not sign attachment URLs", error);
      }
      const expiresAt = now() + SIGNED_URL_TTL_SECONDS * 1000;
      for (const item of data ?? []) {
        // A file the user may not read (another team) gets no signed URL
        if (item.path && item.signedUrl && !item.error) {
          signedUrls.set(item.path, item.signedUrl);
          cache.set(item.path, { url: item.signedUrl, expiresAt });
        }
      }
    }

    return replaceBucketUrls(value, bucket, signedUrls);
  };
};
